"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeEventProbabilitiesByMarketV2 = void 0;
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const _reduxjs_1 = require("../../../@reduxjs");
const types_1 = require("../../../../containers/ConfigProvider/types");
const getCategoryMarketInfoFilteredTradingStatus_1 = require("../getCategoryMarketInfoFilteredTradingStatus");
const isParameterValueMatching_1 = require("../isParameterValueMatching");
const getEventProbabilityByMarketKey_1 = require("../../getEventProbabilityByMarketKey");
const constants_1 = require("../../constants");
const getProbabilityOutcomeNameV2_1 = require("./getProbabilityOutcomeNameV2");
const getEventProbabilityOutcomeNameV2_1 = require("./getEventProbabilityOutcomeNameV2");
const transformParamValueV2 = (templateId) => {
    const withPlus = constants_1.TEMPLATES_WITH_PLUS_SIGN.includes(templateId);
    const withParentheses = constants_1.TEMPLATES_WITH_PARENTHESES.includes(templateId);
    return (value) => {
        let result = value;
        if (withPlus && !value.startsWith('-') && Number(value) !== 0) {
            result = `+${result}`;
        }
        if (withParentheses) {
            result = `(${result})`;
        }
        return result;
    };
};
const mapOutcomes = (probsByOutcomeType, outcomeTypes, getOutcomeName, transformParamValue, players) => {
    return outcomeTypes.flatMap((outcomeType) => {
        const probs = probsByOutcomeType[outcomeType.id] || [];
        return probs.map((eventProbability) => {
            const name = (0, getEventProbabilityOutcomeNameV2_1.getEventProbabilityOutcomeNameV2)({
                eventProbability,
                name: getOutcomeName(outcomeType),
                transformParamValueFunc: transformParamValue,
                players,
            });
            const probability = {
                ...eventProbability,
                outcomeTypeData: {
                    ...outcomeType,
                    name,
                },
            };
            return probability;
        });
    });
};
const groupByOutcomeTypeId = (probabilities) => {
    return probabilities.reduce((acc, cur) => {
        if (acc[cur.outcomeTypeId] === undefined) {
            acc[cur.outcomeTypeId] = [];
        }
        acc[cur.outcomeTypeId].push(cur);
        return acc;
    }, {});
};
/*
 Utility function to compute and store event probabilities by market considering diffs.
 Iterates through each market and its categories to filter and retain only those
 probabilities that are currently trading and match the specified parameters.
 The results are then stored in a provided dictionary, keyed by a specific format
 for easy access and retrieval.
 */
/*
Filter by status and parameter, to exclude sitation when we hav information about market with same id but difference parameters
*/
const findCorrectProbabilitiesByStatusAndParams = (probabilities, marketParameters) => {
    return probabilities.filter((probability) => !constants_1.EVENT_PROBABILITY_EXCLUDE_TRADING_STATUSES.includes(probability.tradingStatus) &&
        marketParameters.every((categoryMarketInfoParameter) => probability.parameters.some((pParameter) => {
            return (0, isParameterValueMatching_1.isParameterValueMatching)(pParameter, categoryMarketInfoParameter);
        })));
};
const MOBILE_DEVICES = new Set([
    types_1.EClientTypes.WEB_MOBILE,
    types_1.EClientTypes.NATIVE_IOS,
    types_1.EClientTypes.NATIVE_ANDROID,
]);
const hasShortName = ({ categoryMarketInfo, clientType, }) => {
    if (MOBILE_DEVICES.has(clientType)) {
        return categoryMarketInfo.shortNamedMobile;
    }
    else {
        return categoryMarketInfo.shortNamed;
    }
};
const computeEventProbabilitiesByMarketV2 = ({ eventId, sportId, eventStatus, marketCategoriesMap, marketsMap, playersMap, categoryMarketInfosMap, eventCompetitors, eventMarkets, eventProbabilitiesByMarket, marketCategoriesInfos, clientType, }) => {
    const marketKeys = new Set();
    const changedMarkets = {};
    const prevEventProbsByMarket = eventProbabilitiesByMarket !== null && eventProbabilitiesByMarket !== void 0 ? eventProbabilitiesByMarket : {};
    const prevMarketCategoriesInfos = marketCategoriesInfos !== null && marketCategoriesInfos !== void 0 ? marketCategoriesInfos : [];
    const eventMarketCategoriesInfosMap = {};
    eventMarkets.forEach(({ marketId, probabilities }) => {
        var _a;
        // We must filter by sport id due to eventMarket can have market id that can belongs to category that doesn't belong to event sport
        const categoryInfos = ((_a = categoryMarketInfosMap[marketId]) !== null && _a !== void 0 ? _a : []).filter(({ marketCategory }) => marketCategory.sportIds.includes(sportId));
        categoryInfos.forEach(({ categoryMarketInfo, marketCategory }) => {
            var _a, _b;
            const marketCategoryId = marketCategory.id;
            const categoryMarketInfoId = categoryMarketInfo.id;
            // Step 3: Filter probabilities according to parameters matching
            const tradingProbabilities = findCorrectProbabilitiesByStatusAndParams(probabilities, categoryMarketInfo.parameters);
            if (!tradingProbabilities.length) {
                // eslint-disable-next-line no-useless-return
                return;
            }
            else {
                const orderedOutcomeTypes = (_b = (_a = marketsMap[marketId]) === null || _a === void 0 ? void 0 : _a.orderedOutcomeTypes) !== null && _b !== void 0 ? _b : [];
                const getOutcomeName = (0, getProbabilityOutcomeNameV2_1.getProbabilityOutcomeNameV2)({
                    competitors: eventCompetitors,
                    isShortNamed: hasShortName({
                        categoryMarketInfo,
                        clientType,
                    }),
                });
                const transformParamValue = transformParamValueV2(categoryMarketInfo.templateId);
                // Computing key
                const key = (0, getEventProbabilityByMarketKey_1.getEventProbabilityByMarketKey)({
                    marketId,
                    categoryMarketInfoId,
                });
                // Add the key to the set of new keys
                marketKeys.add(key);
                // Computing value
                const grouped = groupByOutcomeTypeId(tradingProbabilities);
                const newProbabilities = mapOutcomes(grouped, orderedOutcomeTypes, getOutcomeName, transformParamValue, playersMap);
                const newEventProbabilitiesByMarket = {
                    eventId,
                    marketCategoryId,
                    marketId,
                    categoryMarketInfoId,
                    probabilities: newProbabilities,
                };
                const prevEventProbabilitiesByMarket = prevEventProbsByMarket[key];
                if (!(0, react_fast_compare_1.default)(prevEventProbabilitiesByMarket, newEventProbabilitiesByMarket)) {
                    changedMarkets[key] = newEventProbabilitiesByMarket;
                }
                const eventMarketCategoriesInfosValue = {
                    ...categoryMarketInfo,
                    tradingStatus: (0, getCategoryMarketInfoFilteredTradingStatus_1.getCategoryMarketInfoFilteredTradingStatus)({
                        probabilities: newProbabilities,
                        eventStatus,
                    }),
                };
                if (!eventMarketCategoriesInfosMap[marketCategoryId]) {
                    eventMarketCategoriesInfosMap[marketCategoryId] = {
                        [categoryMarketInfoId]: eventMarketCategoriesInfosValue,
                    };
                }
                else {
                    eventMarketCategoriesInfosMap[marketCategoryId][categoryMarketInfoId] = eventMarketCategoriesInfosValue;
                }
            }
        });
    });
    const prevKeys = Object.keys(prevEventProbsByMarket);
    const deletedMarkets = prevKeys.filter((key) => !marketKeys.has(key));
    const newMarketCategoriesInfos = marketCategoriesMap.sortedIds.reduce((acc, marketCategoryId) => {
        var _a, _b;
        const mci = eventMarketCategoriesInfosMap[marketCategoryId];
        if (!mci) {
            return acc;
        }
        const orderedMarketIds = ((_a = marketCategoriesMap.entries[marketCategoryId]) === null || _a === void 0 ? void 0 : _a.orderedMarketsIds) || [];
        const values = (0, _reduxjs_1.getValues)(orderedMarketIds, mci);
        const categoryMarketInfos = values.map((categoryMarketInfo) => ({
            ...categoryMarketInfo,
            title: categoryMarketInfo.title || '', // to exclude null in title from invalid data
        }));
        acc.push({
            marketCategoryId,
            marketCategoryName: ((_b = marketCategoriesMap.entries[marketCategoryId]) === null || _b === void 0 ? void 0 : _b.name) || '',
            categoryMarketInfos,
        });
        return acc;
    }, []);
    let changedMarketCategoriesInfos;
    if (!(0, react_fast_compare_1.default)(prevMarketCategoriesInfos, newMarketCategoriesInfos)) {
        changedMarketCategoriesInfos = newMarketCategoriesInfos;
    }
    return {
        changedMarkets,
        deletedMarkets,
        changedMarketCategoriesInfos,
    };
};
exports.computeEventProbabilitiesByMarketV2 = computeEventProbabilitiesByMarketV2;
//# sourceMappingURL=computeEventProbabilitiesByMarketV2.js.map